export type NewsletterTextAlignment = "left" | "center" | "right" | "justify";

export type ReleaseChannel = "alpha" | "beta" | "stable" | string;

export type AppManifest = {
  app_id: string;
  slug: string;
  name: string;
  short_name: string;
  description: string;
  tagline: string;
  capabilities: string[];
  mount_path: string;
  standalone: boolean;
  release_channel: ReleaseChannel;
  app_version: string;
  schema_version?: number;
};

export type WorkspacePayload = {
  workspace: {
    name: string;
    version: string;
    release_channel: ReleaseChannel;
  };
  apps: AppManifest[];
};

export type BridgeEvent<TPayload = Record<string, unknown>> = {
  topic: string;
  payload: TPayload;
  sourceAppId: string | null;
  createdAt: string;
};

export type ShellContextPayload = {
  activeAppId: string;
  apps: Array<Pick<AppManifest, "app_id" | "name" | "mount_path" | "capabilities">>;
  recentEvents: BridgeEvent[];
};

export const NEWSLETTER_COMPOSE_STORAGE_KEY = "easy-newsletter:compose-draft";

export type NewsletterComposeAssistantSectionLayout = "left" | "right" | "stacked";

export type NewsletterComposeAssistantSection = {
  text_align?: NewsletterTextAlignment;
  id: string;
  title: string;
  body: string;
  body_html?: string;
  image_url: string;
  image_alt: string;
  button_label: string;
  button_url: string;
  media_layout?: NewsletterComposeAssistantSectionLayout;
  media_width_ratio?: number | null;
  media_height?: number | null;
};

export type NewsletterComposeAssistantDraft = {
  text_align?: NewsletterTextAlignment;
  template_id: "digest" | "story" | "events";
  theme: {
    style: "modern" | "classic" | "festive" | "editorial" | "minimal" | "bulletin";
    primary_color: string;
    accent_color: string;
  };
  header_logo_url?: string;
  header_logo_alt?: string;
  intro_kicker: string;
  intro_title: string;
  intro_body: string;
  intro_body_html?: string;
  intro_image_url?: string;
  intro_image_alt?: string;
  intro_image_layout?: NewsletterComposeAssistantSectionLayout;
  intro_image_width_ratio?: number | null;
  intro_image_height?: number | null;
  sections: NewsletterComposeAssistantSection[];
  closing_note: string;
  closing_note_html?: string;
};

export type NewsletterDefaultDesignSection = {
  text_align?: NewsletterTextAlignment;
  media_layout?: NewsletterComposeAssistantSectionLayout;
  media_width_ratio?: number | null;
  media_height?: number | null;
  has_button?: boolean;
  has_image?: boolean;
};

export type NewsletterDefaultDesignSnapshot = {
  text_align?: NewsletterTextAlignment;
  schema_version: 1 | 2;
  source_campaign_id: number;
  source_campaign_name: string;
  source_campaign_status: string;
  source_campaign_updated_at?: string;
  template_id: NewsletterComposeAssistantDraft["template_id"];
  theme: NewsletterComposeAssistantDraft["theme"];
  header_logo_url?: string;
  header_logo_alt?: string;
  intro_image_url?: string;
  intro_image_alt?: string;
  intro_image_layout?: NewsletterComposeAssistantSectionLayout;
  intro_image_width_ratio?: number | null;
  intro_image_height?: number | null;
  sections: NewsletterDefaultDesignSection[];
  closing_note?: string;
  closing_note_html?: string;
};

export type NewsletterComposeDraft = {
  source: "event-planner";
  name: string;
  subject: string;
  html_body: string;
  assistant_draft?: NewsletterComposeAssistantDraft;
  created_at: string;
  source_event_ids: number[];
  period_start: string;
  period_end: string;
};
